import { useState } from "react";
import { ImageOff } from "lucide-react";

import { cn } from "@/shared/lib/utils";

export interface MediaThumbProps {
  url?: string | null;
  kind?: "image" | "video";
  alt?: string;
  ratio?: "portrait" | "video" | "square";
  className?: string;
  /**
   * 无封面时的占位内容(默认破图 icon)。本就没有视觉封面的产物(B5.5 纯音频)传语义占位,
   * 否则读起来是「图挂了」而不是「这类产物没有图」。
   */
  placeholder?: React.ReactNode;
}

const RATIO: Record<NonNullable<MediaThumbProps["ratio"]>, string> = {
  portrait: "aspect-[4/5]",
  video: "aspect-video",
  square: "aspect-square",
};

/**
 * 统一媒体封面:任意画幅走「模糊铺底 + object-contain」(竖版不留黑边,承袭 video CoverFrame),
 * 无 url(或图片 404)走占位 fallback。image → `<img loading=lazy>` 带 onError 回退(presign 过期不留破图);
 * video → 静音内联 `<video>`(不自动播,预览在 lightbox)。
 */
export function MediaThumb({
  url,
  kind = "image",
  alt = "",
  ratio = "video",
  className,
  placeholder,
}: MediaThumbProps) {
  // 失败态绑定到具体 url(而非布尔),url 变化(切形象/重签 presign)时自然复位,不把新图卡在 fallback。codex C P2。
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const show = !!url && failedUrl !== url;
  return (
    <div className={cn("relative overflow-hidden bg-muted", RATIO[ratio], className)}>
      {show ? (
        kind === "image" ? (
          <>
            {/* 模糊铺底:同源放大模糊填满,主体 contain 居中,画幅无关不留黑边 */}
            <img
              src={url!}
              alt=""
              aria-hidden
              className="absolute inset-0 h-full w-full scale-110 object-cover opacity-60 blur-lg"
            />
            <img
              src={url!}
              alt={alt}
              loading="lazy"
              onError={() => setFailedUrl(url!)}
              className="relative h-full w-full object-contain"
            />
          </>
        ) : (
          <video
            src={url!}
            muted
            playsInline
            preload="metadata"
            className="relative h-full w-full object-contain"
          />
        )
      ) : (
        <div
          data-testid="media-thumb-fallback"
          className="flex h-full w-full items-center justify-center text-muted-foreground/50"
        >
          {placeholder ?? <ImageOff className="h-8 w-8" />}
        </div>
      )}
    </div>
  );
}
